import { createContext, useContext } from 'react';

/**
 * Context для данных отчета FormPF
 * Предоставляет доступ к данным и методам из useReport хука
 * без необходимости передавать их через пропсы через все уровни компонентов
 *
 * BEST PRACTICES:
 * - Использует useMemo для оптимизации производительности
 * - Разделяет стабильные данные и функции от часто меняющихся данных
 */
export const ReportContext = createContext(null);

/**
 * Хук для использования контекста отчета
 * @returns {object} Данные и методы из useReport хука
 * @throws {Error} Если используется вне ReportContextProvider
 */
export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within ReportContextProvider');
  }
  return context;
};

/**
 * Опциональный хук для безопасного использования контекста
 * Возвращает null если контекст недоступен (для обратной совместимости)
 */
export const useReportContextOptional = () => {
  return useContext(ReportContext);
};

/**
 * Провайдер контекста отчета
 *
 * BEST PRACTICE: value должен быть мемоизирован в родительском компоненте
 * (ReportContainer) с помощью useMemo для предотвращения лишних ререндеров
 */
export const ReportContextProvider = ({ children, value }) => {
  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
};
